import { PublicLayout } from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Play,
  BookOpen,
  Zap,
  TrendingUp,
  Users,
  Award,
  Smartphone,
  Headphones,
  Mic,
  ChevronRight,
  Calendar,
  Clock,
  Check,
} from "lucide-react";
import { Link } from "react-router-dom";
import { AnimatedSoundWaves } from "@/components/ui/AnimatedSoundWaves";
import { AudioDemoSection } from "@/components/sections/AudioDemoSection";
import { AIVoicesSection } from "@/components/sections/AIVoicesSection";
import { Carousel, CarouselContent, CarouselItem, CarouselNext } from "@/components/ui/carousel";

const Index = () => {
  return (
    <PublicLayout
      showHero={true}
      heroTitle={
        <>
          In <span className="text-blue-200/80">7 Minuten</span> vom Gedanken zum gefeierten Hörbuch
        </>
      }
      heroSubtitle="Erstelle dein Hörbuch in Rekordzeit – mit modernsten Native AI-Stimmen, die echter klingen als menschliche Sprecher, und Veröffentlichung auf allen großen Portalen."
      heroVariant="default"
      heroShowStatistics={true}
      heroShowTrustIndicators={true}
      heroChildren={
        <div className="flex justify-center mb-12">
          <Button
            size="lg"
            className="bg-hero-overlay text-white border-2 border-white hover:bg-hero-overlay hover:text-white px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            onClick={() =>
              document.getElementById("so-funktioniert-es")?.scrollIntoView({ behavior: "smooth" })
            }
          >
            Live Präsentation
          </Button>
        </div>
      }
    >
      {/* Hero section is now handled by PublicLayout */}

      {/* Features Section */}
      <section className="py-20 px-4 bg-gray-100">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Revolutionäre KI-Technologie für Ihr Hörbuch-Business
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Nutzen Sie die Kraft der künstlichen Intelligenz für professionelle Hörbucherstellung
              und maximieren Sie Ihre Erfolgschancen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="minimal-card hover-lift">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mb-6">
                  <Zap className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Erstellung in 7 Minuten</h3>
                <p className="text-gray-600 leading-relaxed">
                  Von der Idee zum fertigen Hörbuch in unter 7 Minuten. Schneller war
                  Hörbuch-Publishing noch nie.
                </p>
              </CardContent>
            </Card>

            <Card className="minimal-card hover-lift">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mb-6">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Emotionale KI-Stimmen</h3>
                <p className="text-gray-600 leading-relaxed">
                  Menschliche, emotionale KI-Stimmen die Ihre Zuhörer fesseln und für ein
                  professionelles Hörerlebnis sorgen.
                </p>
              </CardContent>
            </Card>

            <Card className="minimal-card hover-lift">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mb-6">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">SEO & Ranking-Analyse</h3>
                <p className="text-gray-600 leading-relaxed">
                  Automatisierte Keyword-Recherche und Inhaltsoptimierung für maximale Sichtbarkeit
                  und Verkaufserfolg.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* AI Voices Section */}
      <AIVoicesSection />

      {/* Audio Demo Section */}
      <AudioDemoSection />

      {/* Alle Funktionen Section */}
      <section id="funktionen" className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Alle Funktionen im Überblick
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Von der Ideenfindung bis zur Veröffentlichung – unsere KI unterstützt Sie bei jedem
              Schritt.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="minimal-card hover-lift">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mb-6">
                  <Zap className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Blitzschnelle Erstellung</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Erstellen Sie professionelle Hörbücher in nur 7 Minuten mit unserer
                  fortschrittlichen KI-Technologie.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li>• Automatische Textgenerierung</li>
                  <li>• Sofortige Sprachsynthese</li>
                  <li>• Qualitätskontrolle in Echtzeit</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="minimal-card hover-lift">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mb-6">
                  <Mic className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Natürliche KI-Stimmen</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Über 200 verschiedene Stimmen in mehreren Sprachen für authentische Hörbücher.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li>• Emotionale Sprachmodulation</li>
                  <li>• Mehrsprachige Unterstützung</li>
                  <li>• Anpassbare Sprechgeschwindigkeit</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="minimal-card hover-lift">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mb-6">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">SEO-Optimierung</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Automatische Keyword-Analyse und Optimierung für maximale Sichtbarkeit.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li>• Trending Topics Analyse</li>
                  <li>• Automatische Beschreibungen</li>
                  <li>• Optimierte Titel-Generierung</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="minimal-card hover-lift">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mb-6">
                  <Smartphone className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Multi-Platform Publishing</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Veröffentlichen Sie auf allen wichtigen Plattformen mit nur einem Klick.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li>• Audible Integration</li>
                  <li>• Spotify for Podcasters</li>
                  <li>• Apple Podcasts & Google</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="minimal-card hover-lift">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mb-6">
                  <Award className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Cover-Design KI</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Professionelle Cover werden automatisch basierend auf Ihrem Inhalt erstellt.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li>• Themen-angepasste Designs</li>
                  <li>• Hochauflösende Grafiken</li>
                  <li>• Genre-spezifische Stile</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="minimal-card hover-lift">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mb-6">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Analytics & Insights</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Detaillierte Analysen zur Performance Ihrer Hörbücher und Optimierungsvorschläge.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li>• Echtzeit-Statistiken</li>
                  <li>• Zielgruppen-Analyse</li>
                  <li>• Umsatz-Tracking</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* So funktioniert's Section */}
      <section id="so-funktioniert-es" className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">So funktioniert's</h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
              In nur 3 einfachen Schritten zum erfolgreichen Hörbuch
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Thema eingeben</h3>
              <p className="text-gray-600 leading-relaxed">
                Geben Sie einfach Ihr gewünschtes Thema oder eine Idee ein. Unsere KI analysiert
                sofort die Marktchancen.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">KI erstellt Hörbuch</h3>
              <p className="text-gray-600 leading-relaxed">
                Unsere fortschrittliche KI erstellt automatisch Inhalt, Sprache, Cover und
                optimierte Beschreibungen.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Automatisch veröffentlichen</h3>
              <p className="text-gray-600 leading-relaxed">
                Mit einem Klick wird Ihr Hörbuch auf allen wichtigen Plattformen veröffentlicht und
                optimiert.
              </p>
            </div>
          </div>

          {/* Separator Line */}
          <div className="border-t border-gray-300 mt-16 mb-12"></div>

          {/* Call to Action Text */}
          <div className="text-center">
            <div className="mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Starte jetzt mit Audio Empire – und veröffentliche noch heute dein erstes Hörbuch!
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                Melde dich kostenlos zu unserer täglichen Live-Präsentation an und erlebe Schritt
                für Schritt, wie du in kürzester Zeit ein professionelles Hörbuch erstellst.
              </p>
              <Button
                size="lg"
                className="bg-gray-900 text-white hover:bg-gray-800 px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                Jetzt anmelden
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Was unsere Publisher sagen
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Echte Erfolgsgeschichten von Autoren, die mit Audio Empire durchgestartet sind.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <Card className="minimal-card">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-full mr-4 overflow-hidden bg-gray-200">
                    <img
                      src="https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=80&h=80&fit=crop&crop=face&auto=format&q=80&sat=-100"
                      alt="Sarah M. Profilbild"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Sarah M.</h4>
                    <p className="text-gray-600 text-sm">Bestseller-Autorin</p>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  "In nur 7 Minuten hatte ich mein erstes Hörbuch erstellt. Die KI-Stimme klingt so
                  natürlich, dass meine Hörer dachten, ich hätte es selbst eingesprochen!"
                </p>
                <div className="flex text-yellow-400">★★★★★</div>
              </CardContent>
            </Card>

            <Card className="minimal-card">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-full mr-4 overflow-hidden bg-gray-200">
                    <img
                      src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face&auto=format&q=80&sat=-100"
                      alt="Marcus K. Profilbild"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Marcus K.</h4>
                    <p className="text-gray-600 text-sm">Online-Entrepreneur</p>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  "Bereits im ersten Monat über €3.500 Einnahmen! Die SEO-Optimierung und
                  automatische Veröffentlichung haben mein Hörbuch sofort sichtbar gemacht."
                </p>
                <div className="flex text-yellow-400">★★★★★</div>
              </CardContent>
            </Card>

            <Card className="minimal-card">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-full mr-4 overflow-hidden bg-gray-200">
                    <img
                      src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face&auto=format&q=80&sat=-100"
                      alt="Jennifer L. Profilbild"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Jennifer L.</h4>
                    <p className="text-gray-600 text-sm">Coach & Trainerin</p>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  "Endlich kann ich meine Expertise als Hörbuch teilen, ohne tagelang im Studio zu
                  stehen. Audio Empire hat alles für mich übernommen!"
                </p>
                <div className="flex text-yellow-400">★★★★★</div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="minimal-card">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-full mr-4 overflow-hidden bg-gray-200">
                    <img
                      src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face&auto=format&q=80&sat=-100"
                      alt="Thomas W. Profilbild"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Thomas W.</h4>
                    <p className="text-gray-600 text-sm">Finanz-Experte</p>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  "Mein Hörbuch über Finanztipps hat sich über 5.000 Mal verkauft! Die automatische
                  Keyword-Optimierung hat perfekt funktioniert."
                </p>
                <div className="flex text-yellow-400">★★★★★</div>
              </CardContent>
            </Card>

            <Card className="minimal-card">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-full mr-4 overflow-hidden bg-gray-200">
                    <img
                      src="https://images.unsplash.com/photo-1494790108755-2616b612b786?w=80&h=80&fit=crop&crop=face&auto=format&q=80&sat=-100"
                      alt="Lisa P. Profilbild"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Lisa P.</h4>
                    <p className="text-gray-600 text-sm">Lifestyle-Bloggerin</p>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  "Die Veröffentlichung auf allen Plattformen gleichzeitig hat mir so viel Zeit
                  gespart. Binnen einer Woche war ich auf Audible sichtbar!"
                </p>
                <div className="flex text-yellow-400">★★★★★</div>
              </CardContent>
            </Card>

            <Card className="minimal-card">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-full mr-4 overflow-hidden bg-gray-200">
                    <img
                      src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=80&h=80&fit=crop&crop=face&auto=format&q=80&sat=-100"
                      alt="Alexander R. Profilbild"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Alexander R.</h4>
                    <p className="text-gray-600 text-sm">Business-Coach</p>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  "Als jemand der keine Zeit für aufwendige Aufnahmen hat, ist Audio Empire ein
                  Gamechanger. Professionelle Qualität in Minuten!"
                </p>
                <div className="flex text-yellow-400">★★★★★</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Preise Section */}
      <section
        id="preise"
        className="py-20 px-4 bg-gray-900 text-white relative overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('/lovable-uploads/2c916b40-43c9-4dc9-9c1d-3b61266c291c.png')`,
        }}
      >
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-gray-900/20"></div>
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white drop-shadow-lg">
              Preise & Pakete
            </h2>
            <p className="text-lg text-gray-200 leading-relaxed max-w-2xl mx-auto drop-shadow">
              Finden Sie das perfekte Paket für Ihre Hörbuch-Produktion
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {(() => {
              const plans = [
                {
                  name: "Basic",
                  originalPrice: "200€",
                  price: "160€",
                  description: "Für Einsteiger & Hörbuch-Held*innen",
                  features: [
                    "100 Credits (einmalig)",
                    "Bis zu 10 Hörbücher",
                    "Standard Publishing-Speed",
                    "Sofort startklar",
                  ],
                  popular: false,
                },
                {
                  name: "Plus",
                  originalPrice: "340€",
                  price: "325€",
                  description: "Für Macher, die mehr Reichweite wollen",
                  features: [
                    "250 Credits (einmalig)",
                    "Bis zu 25 Hörbücher",
                    "Priority Credits",
                    "Fastlane Upload",
                    "Exklusive Platform-Boni",
                  ],
                  popular: true,
                },
                {
                  name: "Pro",
                  originalPrice: "600€",
                  price: "550€",
                  description: "Für Publisher & Vollprofis, die skalieren",
                  features: [
                    "500 Credits (einmalig)",
                    "Bis zu 50 Hörbücher",
                    "Maximale Priority Credits",
                    "Premium Upload",
                    "VIP Support",
                  ],
                  popular: false,
                },
              ];

              return plans.map((plan, index) => (
                <Card
                  key={index}
                  className={`relative backdrop-blur-md border shadow-2xl transition-all duration-300 hover:scale-105 hover:shadow-3xl flex flex-col h-full ${
                    plan.popular
                      ? "bg-gray-800/40 border-gray-500/50"
                      : "bg-gray-800/30 border-gray-600/30"
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-gray-100 text-gray-900 px-4 py-1 text-sm font-semibold">
                        Beliebteste Wahl
                      </Badge>
                    </div>
                  )}

                  <CardContent className="p-8 flex flex-col h-full">
                    <div className="text-center pb-4 flex-shrink-0">
                      <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                      <div className="mb-4">
                        <div className="flex items-center justify-center space-x-2">
                          <span className="text-gray-400 line-through text-lg">
                            {plan.originalPrice}
                          </span>
                          <span className="text-4xl font-bold text-white">{plan.price}</span>
                        </div>
                        <div className="text-gray-300 text-sm mt-1">pro Monat</div>
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed">{plan.description}</p>
                    </div>

                    <div className="space-y-3 mb-6 flex-1">
                      {plan.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-start space-x-3">
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                            style={{ backgroundColor: "#ebf5ff" }}
                          >
                            <Check className="h-3 w-3 text-gray-900" />
                          </div>
                          <span className="text-gray-200 text-sm leading-relaxed">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="text-center mb-6">
                      <p className="text-white font-bold text-lg">
                        {plan.name === "Basic" && "16€ pro Hörbuch"}
                        {plan.name === "Plus" && "13€ pro Hörbuch"}
                        {plan.name === "Pro" && "11€ pro Hörbuch"}
                      </p>
                    </div>

                    <Button
                      className={`w-full font-semibold py-3 text-gray-900 transition-all duration-300 mt-auto ${
                        plan.popular ? "hover:bg-opacity-90" : "hover:bg-opacity-90"
                      }`}
                      style={{ backgroundColor: "#ebf5ff" }}
                      size="lg"
                      onClick={() =>
                        document
                          .getElementById("so-funktioniert-es")
                          ?.scrollIntoView({ behavior: "smooth" })
                      }
                    >
                      Jetzt starten
                    </Button>
                  </CardContent>
                </Card>
              ));
            })()}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};
export default Index;
